import { readInventoryField } from '../compile/inventory-raw-readers.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

/** Prepare cold floor rows before a synchronous walk/drop plan; pin fetched templates on the rows that need them. */
export function hydrateInventoryDefinitions(rows: Row[], catalog: unknown, fetch: (id: string) => Promise<unknown>): Promise<void> | null {
  const missing = new Map<string, Row[]>();
  for (const row of rows) {
    if (inventoryDefinition(row, catalog)) continue;
    const id = String(readInventoryField(row, 'inventory_item_id') ?? '').trim().toUpperCase();
    if (!/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)) continue;
    const uses = missing.get(id);
    if (uses) uses.push(row); else missing.set(id, [row]);
  }
  return missing.size ? Promise.all([...missing].map(async ([id, uses]) => {
    const definition = record(await fetch(id).catch(() => null));
    if (definition && record(definition.items)) for (const row of uses)
      row.catalog = { ...record(row.catalog), [id]: definition };
  })).then(() => undefined) : null;
}

/** The same held template in scene, movement, cover and area readers, including warm boots with an empty page catalog. */
export function inventoryDefinition(row: Row, catalog: unknown, read?: (id: string) => Row | null | undefined): Row | null {
  const id = String((record(row.inventory) ?? row).inventory_item_id ?? '').trim().toUpperCase();
  const host = globalThis as unknown as { window?: { FateTide?: { objectCache?: Map<string, unknown> } }; FateTide?: { objectCache?: Map<string, unknown> } };
  const cache = host.window?.FateTide?.objectCache ?? host.FateTide?.objectCache;
  for (const lookup of [() => record(row.catalog)?.[id], () => record(catalog)?.[id], () => read?.(id), () => cache?.get(id)]) {
    const definition = record(lookup());
    if (definition && record(definition.items)) return definition;
  }
  return null;
}

/** Attach the resolved template as well as row-local mesh/state definitions to the selector and layer readers. */
export function inventoryCatalog(row: Row, catalog: unknown, definition = inventoryDefinition(row, catalog)): Row {
  const id = String((record(row.inventory) ?? row).inventory_item_id ?? '').trim().toUpperCase();
  return { ...record(catalog), ...record(row.catalog), ...(definition ? { [id]: definition } : {}) };
}
